import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, tap, catchError, throwError, map, of, switchMap, from } from 'rxjs';
import { environment } from '../../../environments/environment';
import { User, UserProfile, AuthState, LoginRequest, LoginResponse, ApiResponse, Role } from '../models/user.model';
import { getSubdomain } from '../utils/subdomain.util';
import { AREA_POR_ROL, AreaApp, RUTA_INICIO_POR_AREA } from '../config/menu.catalog';
import { ModuleAccessService } from './module-access.service';
import { PermissionService } from './permission.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly API_URL = environment.apiUrlV1;
  private readonly AUTH_KEY = 'uvirtual_auth';
  private readonly TOKEN_KEY = 'authToken';
  private readonly ACTIVE_ROLE_KEY = 'activeRole';

  // Estado global de autenticación
  private authState = new BehaviorSubject<AuthState>({
    user: null,
    profile: null,
    isAuthenticated: false,
    isLoading: true,
    error: null
  });

  // ✅ Nuevo: indica si los permisos ya fueron cargados
  private permissionsLoaded = new BehaviorSubject<boolean>(false);

  // Nuevo estado para manejar el rol seleccionado
  private activeRoleSubject = new BehaviorSubject<Role | null>(null);

  public authState$ = this.authState.asObservable();
  public permissionsLoaded$ = this.permissionsLoaded.asObservable();
  public activeRole$ = this.activeRoleSubject.asObservable();

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: any,
    private moduleAccessService: ModuleAccessService,
    private permissionService: PermissionService
  ) {
    this.initializeAuth();
  }

  private initializeAuth(): void {
    if (this.isBrowser) {
      // El rol activo se restaura antes que nada: de él dependen los permisos
      // que se cargan y el menú que se pinta al recargar la página.
      this.activeRoleSubject.next(this.getStoredActiveRole());

      const token = this.getToken();
      const savedUser = this.getStoredUser();

      if (token && savedUser) {
        this.authState.next({
          user: savedUser,
          profile: this.mapToProfile(savedUser),
          isAuthenticated: true,
          isLoading: true,
          error: null
        });

        this.verifyToken().subscribe({
          next: valid => {
            if (!valid) {
              this.cerrarSesion();
            }
            // verifyToken ya marca permissionsLoaded = true
          },
          error: () => this.cerrarSesion()
        });
      } else {
        // Sin token: no hay permisos que cargar
        this.authState.next({
          user: null,
          profile: null,
          isAuthenticated: false,
          isLoading: false,
          error: null
        });
        this.permissionsLoaded.next(true); // ✅ marcar como "listo" inmediatamente
      }
    } else {
      this.authState.next({
        user: null,
        profile: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      });
      this.permissionsLoaded.next(true);
    }
  }

  // --- NUEVOS MÉTODOS PARA MANEJAR EL PERFIL ---

  /**
   * Fija el rol con el que el usuario va a trabajar y carga solo sus permisos.
   * Un usuario con varios roles ve únicamente los módulos del rol elegido.
   */
  setActiveRole(role: Role): void {
    if (this.isBrowser) {
      localStorage.setItem(this.ACTIVE_ROLE_KEY, JSON.stringify(role));
    }
    this.activeRoleSubject.next(role);
    this.aplicarPermisosDelRol(role);
  }

  /** Área de la aplicación del rol activo. Sin rol activo, no hay área. */
  getActiveArea(): AreaApp | null {
    const rol = this.activeRoleSubject.value;
    return rol ? (AREA_POR_ROL[rol.name.toUpperCase()] ?? null) : null;
  }

  /** Ruta de inicio del rol activo; el login redirige aquí. */
  getHomeRoute(): string {
    const area = this.getActiveArea();
    return area ? RUTA_INICIO_POR_AREA[area] : '/auth/select-profile';
  }

  private aplicarPermisosDelRol(role: Role): void {
    this.permissionService.setUserPermissions(role.permissions ?? []);
  }

  /**
   * Carga los permisos que corresponden: los del rol activo si hay uno, y si
   * no la unión de todos, que es lo único que se puede afirmar del usuario.
   */
  private aplicarPermisos(user: User): void {
    const activo = this.activeRoleSubject.value;
    const rol = activo
      ? user.roles?.find(r => r.id === activo.id || r.name === activo.name)
      : user.roles?.length === 1
        ? user.roles[0]
        : null;

    if (rol) {
      // El rol guardado puede traer permisos viejos: mandan los del servidor.
      this.activeRoleSubject.next(rol);
      if (this.isBrowser) {
        localStorage.setItem(this.ACTIVE_ROLE_KEY, JSON.stringify(rol));
      }
      this.aplicarPermisosDelRol(rol);
      return;
    }

    this.permissionService.setUserPermissions(user.permissions || []);
  }

  getActiveRole(): Role | null {
    return this.activeRoleSubject.value;
  }

  private getStoredActiveRole(): Role | null {
    if (this.isBrowser) {
      const stored = localStorage.getItem(this.ACTIVE_ROLE_KEY);
      return stored ? JSON.parse(stored) : null;
    }
    return null;
  }

  /**
   * Login de usuario
   */
  login(credentials: LoginRequest): Observable<LoginResponse> {
    this.setLoading(true);
    this.setError(null);
    this.permissionsLoaded.next(false);

    const headers = new HttpHeaders({
      'x-tenant-id': getSubdomain()!,
    });

    return this.http.post<LoginResponse>(`${this.API_URL}/auth/login`, credentials, { headers }).pipe(
      tap(response => {
        this.setToken(response.access_token);
        this.setUser(response.user);
        this.aplicarPermisos(response.user);
        this.permissionsLoaded.next(true);
        this.setLoading(false);
      }),
      catchError(error => {
        this.setError('Credenciales inválidas');
        this.setLoading(false);
        this.permissionsLoaded.next(true);
        return throwError(() => error);
      })
    );
  }
  /**
  * Verificar token con el backend y actualizar usuario si es válido
  */
  verifyToken(): Observable<boolean> {
    return this.http.get<ApiResponse<User>>(`${this.API_URL}/users/user`, {
      headers: this.getAuthHeaders()
    }).pipe(
      map(response => {
        if (response.success && response.data) {
          this.setUser(response.data);
          this.aplicarPermisos(response.data);
          this.permissionsLoaded.next(true);
          return true;
        }
        this.permissionsLoaded.next(true);
        return false;
      }),
      catchError((error) => {
        console.error('❌ Error verificando token:', error);
        this.permissionsLoaded.next(true);
        return of(false);
      })
    );
  }

  cerrarSesion(): void {
    if (this.isBrowser) {
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.AUTH_KEY);
      localStorage.removeItem(this.ACTIVE_ROLE_KEY);
    }
    this.activeRoleSubject.next(null);

    this.authState.next({
      user: null,
      profile: null,
      isAuthenticated: false,
      isLoading: false,
      error: null
    });

    this.permissionsLoaded.next(true); // ✅ resetear
    this.moduleAccessService.clear();
    this.permissionService.flushPermissions(); // ✅ limpiar permisos de ngx-permissions
  }

  guardarTema(theme: 'light' | 'dark'): void {
    const user = this.authState.value.user;
    if (user) {
      const updated = { ...user, theme_preference: theme };
      if (this.isBrowser) {
        localStorage.setItem(this.AUTH_KEY, JSON.stringify(updated));
      }
      this.authState.next({ ...this.authState.value, user: updated });
    }
    this.http.patch<ApiResponse<any>>(`${this.API_URL}/users/theme`, { theme }, {
      headers: this.getAuthHeaders()
    }).subscribe({ error: () => { } });
  }

  // ... resto de métodos sin cambios ...

  obtenerPerfil(): Observable<User> {
    this.setLoading(true);

    return this.http.get<ApiResponse<User>>(`${this.API_URL}/auth/user`, {
      headers: this.getAuthHeaders()
    }).pipe(
      map(response => {
        if (response.success) {
          this.setUser(response.data);
          return response.data;
        } else {
          throw new Error(response.message || 'Error al obtener perfil');
        }
      }),
      tap(() => {
        this.setLoading(false);
      }),
      catchError(error => {
        this.setError('Error al cargar el perfil');
        this.setLoading(false);
        return throwError(() => error);
      })
    );
  }

  obtenerPerfilCompleto(): Observable<any> {
    return this.http.get<ApiResponse<any>>(`${this.API_URL}/users/profile`, {
      headers: this.getAuthHeaders()
    }).pipe(
      map(response => {
        if (response.success) {
          const currentState = this.authState.value;
          this.authState.next({
            ...currentState,
            profile: {
              ...currentState.profile,
              ...response.data
            }
          });
          return response.data;
        } else {
          throw new Error(response.message || 'Error al obtener perfil completo');
        }
      })
    );
  }

  actualizarPerfil(profileData: Partial<UserProfile>): Observable<User> {
    return this.http.patch<ApiResponse<User>>(`${this.API_URL}/users/profile`, profileData, {
      headers: this.getAuthHeaders()
    }).pipe(
      map(response => {
        if (response.success) {
          this.setUser(response.data);
          return response.data;
        } else {
          throw new Error(response.message || 'Error al actualizar perfil');
        }
      })
    );
  }

  hasRole(role: string): boolean {
    const user = this.authState.value.user;
    return user?.roles?.some((r: any) => r.name === role) ?? false;
  }

  hasPermission(permission: string): boolean {
    const user = this.authState.value.user;
    return user?.permissions?.some((p: any) => p === permission) ?? false;
  }

  hasAnyRole(roles: string[]): boolean {
    return roles.some(role => this.hasRole(role));
  }

  hasAllRoles(roles: string[]): boolean {
    return roles.every(role => this.hasRole(role));
  }

  get currentUser(): User | null {
    return this.authState.value.user;
  }

  get currentProfile(): UserProfile | null {
    return this.authState.value.profile;
  }

  get isAuthenticated(): boolean {
    return this.authState.value.isAuthenticated;
  }

  get isLoading(): boolean {
    return this.authState.value.isLoading;
  }

  get error(): string | null {
    return this.authState.value.error;
  }

  private setUser(user: User): void {
    if (this.isBrowser) {
      localStorage.setItem(this.AUTH_KEY, JSON.stringify(user));
    }

    this.authState.next({
      user,
      profile: this.mapToProfile(user),
      isAuthenticated: true,
      isLoading: false,
      error: null
    });
  }

  private setLoading(isLoading: boolean): void {
    const currentState = this.authState.value;
    this.authState.next({
      ...currentState,
      isLoading
    });
  }

  private setError(error: string | null): void {
    const currentState = this.authState.value;
    this.authState.next({
      ...currentState,
      error
    });
  }

  private mapToProfile(user: User): UserProfile {
    return {
      userId: user.id,
      email: user.email,
      firstName: user.firstName,
      firstSurname: user.firstSurname,
      secondName: user.secondName,
      secondSurname: user.secondSurname,
      fullName: `${user.firstName} ${user.firstSurname}`.trim(),
      documentId: user.documentId,
      phone: user.phone,
      isEmailConfirmed: user.isEmailConfirmed
    };
  }

  private getAuthHeaders(): HttpHeaders {
    const token = this.getToken();
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'x-tenant-id': getSubdomain()!,
    });
  }

  public getToken(): string | null {
    if (this.isBrowser) {
      return localStorage.getItem(this.TOKEN_KEY);
    }
    return null;
  }

  private setToken(token: string): void {
    if (this.isBrowser) {
      localStorage.setItem(this.TOKEN_KEY, token);
    }
  }

  private getStoredUser(): User | null {
    if (this.isBrowser) {
      const stored = localStorage.getItem(this.AUTH_KEY);
      return stored ? JSON.parse(stored) : null;
    }
    return null;
  }

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }
}